// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Core__JSON from "@rescript/core/src/Core__JSON.bs.js";
import * as Core__Option from "@rescript/core/src/Core__Option.bs.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as Primitive_exceptions from "@rescript/runtime/lib/es6/Primitive_exceptions.js";
import * as Utils$OrcaPaymentPage from "./Utils.bs.js";

function parseSdkNextActionError(json) {
  let dict = Utils$OrcaPaymentPage.getDictFromJson(json);
  let nextAction = Utils$OrcaPaymentPage.getDictFromDict(dict, "sdk_next_action")["next_action"];
  if (nextAction === undefined) {
    return;
  }
  let str = Core__JSON.Classify.classify(nextAction);
  if (typeof str !== "object") {
    return;
  }
  switch (str.TAG) {
    case "String" :
      if (str._0 === "deny") {
        return "";
      } else {
        return;
      }
    case "Object" :
      return Core__Option.map(str._0["deny"], denyJson => Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromJson(denyJson), "message", ""));
    default:
      return;
  }
}

function parseEligibilitySurchargeDetails(dict) {
  return Core__Option.flatMap(dict["surcharge_details"], surchargeJson => {
    let surchargeDict = Utils$OrcaPaymentPage.getDictFromJson(surchargeJson);
    let surchargeInnerDict = Utils$OrcaPaymentPage.getDictFromDict(surchargeDict, "surcharge");
    let displayTotal = Utils$OrcaPaymentPage.getFloat(surchargeDict, "display_total_surcharge_amount", 0.0);
    if (displayTotal > 0.0) {
      return {
        surcharge: {
          type: Utils$OrcaPaymentPage.getString(surchargeInnerDict, "type", ""),
          value: Utils$OrcaPaymentPage.getFloat(surchargeInnerDict, "value", 0.0)
        },
        taxOnSurcharge: Core__Option.flatMap(surchargeDict["tax_on_surcharge"], v => {
          let n = Core__JSON.Classify.classify(v);
          if (typeof n !== "object" || n.TAG !== "Number") {
            return;
          } else {
            return n._0;
          }
        }),
        displaySurchargeAmount: Utils$OrcaPaymentPage.getFloat(surchargeDict, "display_surcharge_amount", 0.0),
        displayTaxOnSurchargeAmount: Utils$OrcaPaymentPage.getFloat(surchargeDict, "display_tax_on_surcharge_amount", 0.0),
        displayTotalSurchargeAmount: displayTotal
      };
    }
  });
}

function parseEligibilityAmountDetails(dict) {
  return Core__Option.map(Utils$OrcaPaymentPage.getOptionalDict(dict, "amount_details"), amountDetailsDict => ({
    totalAmount: Utils$OrcaPaymentPage.getInt(amountDetailsDict, "total_amount", 0),
    netAmount: Utils$OrcaPaymentPage.getInt(amountDetailsDict, "net_amount", 0),
    currency: Utils$OrcaPaymentPage.getString(amountDetailsDict, "currency", "")
  }));
}

function parseEligibilityOfferDetails(dict) {
  let amountDetails = parseEligibilityAmountDetails(dict);
  return Core__Option.flatMap(Utils$OrcaPaymentPage.getOptionalDict(dict, "offer_details"), offerDetailsDict => {
    let offerQuoteIds = Utils$OrcaPaymentPage.getStrArray(offerDetailsDict, "uplifted_offer_quote_ids").filter(offerQuoteId => offerQuoteId !== "");
    let eligibleOffers = Utils$OrcaPaymentPage.getArrayOfObjectsFromDict(offerDetailsDict, "eligible_offers").map(offerDict => ({
      offerQuoteId: Utils$OrcaPaymentPage.getString(offerDict, "offer_quote_id", ""),
      offerAmount: Utils$OrcaPaymentPage.getInt(offerDict, "offer_amount", 0),
      currency: Utils$OrcaPaymentPage.getString(offerDict, "currency", ""),
      code: Utils$OrcaPaymentPage.getString(offerDict, "code", ""),
      title: Utils$OrcaPaymentPage.getString(offerDict, "title", ""),
      description: Utils$OrcaPaymentPage.getString(offerDict, "description", "")
    })).filter(offer => offer.offerQuoteId !== "");
    if (eligibleOffers.length > 0) {
      return {
        offerQuoteIds: offerQuoteIds,
        eligibleOffers: eligibleOffers,
        amountDetails: amountDetails
      };
    }
  });
}

function parseEligibilityResponse(json) {
  let dict = Utils$OrcaPaymentPage.getDictFromJson(json);
  let eligibilityError = parseSdkNextActionError(json);
  let surchargeDetails = parseEligibilitySurchargeDetails(dict);
  let offerDetails = parseEligibilityOfferDetails(dict);
  return {
    eligibilityError: eligibilityError,
    surchargeDetails: surchargeDetails,
    offerDetails: offerDetails
  };
}

async function performEligibilityCheck(clientSecret, publishableKey, logger, customPodUri, bodyArr, sdkAuthorization, endpoint, signal, setIsEligibilityPending, setEligibilitySurchargeDetails, setEligibilityOfferDetails, setEligibilityError, errorLogMessage, fetchEligibility) {
  setEligibilitySurchargeDetails(param => {});
  setEligibilityOfferDetails(param => {});
  setIsEligibilityPending(param => true);
  try {
    let json = await fetchEligibility(clientSecret, publishableKey, logger, customPodUri, bodyArr, sdkAuthorization, endpoint, signal);
    let match = parseEligibilityResponse(json);
    let offerDetails = match.offerDetails;
    let surchargeDetails = match.surchargeDetails;
    let eligibilityError = match.eligibilityError;
    Core__Option.forEach(setEligibilityError, setter => setter(param => eligibilityError));
    setEligibilitySurchargeDetails(param => surchargeDetails);
    setEligibilityOfferDetails(param => offerDetails);
    return setIsEligibilityPending(param => false);
  } catch (raw_exn) {
    let exn = Primitive_exceptions.internalToException(raw_exn);
    logger.setLogError(Core__Option.getOr(JSON.stringify({
      message: errorLogMessage,
      error: JSON.stringify(exn)
    }), ""), "PAYMENT_METHOD_ELIGIBILITY_CALL", undefined, undefined, undefined, undefined, undefined);
    Core__Option.forEach(setEligibilityError, setter => setter(param => {}));
    return setIsEligibilityPending(param => false);
  }
}

async function startEligibilityCheck(controllerRef, clientSecret, publishableKey, logger, customPodUri, bodyArr, sdkAuthorization, endpoint, setIsEligibilityPending, setEligibilitySurchargeDetails, setEligibilityOfferDetails, setEligibilityError, errorLogMessage, fetchEligibility) {
  Core__Option.forEach(controllerRef.current, c => {
    c.abort();
  });
  let controller = new AbortController();
  controllerRef.current = Primitive_option.some(controller);
  let signal = controller.signal;
  if (clientSecret !== undefined) {
    return await performEligibilityCheck(clientSecret, publishableKey, logger, customPodUri, bodyArr, sdkAuthorization, endpoint, signal, setIsEligibilityPending, setEligibilitySurchargeDetails, setEligibilityOfferDetails, setEligibilityError, errorLogMessage, fetchEligibility);
  } else {
    return setIsEligibilityPending(param => false);
  }
}

function getCardEligibilityErrorText(cardEligibilityError, localeString) {
  if (cardEligibilityError !== undefined && cardEligibilityError !== "") {
    return cardEligibilityError;
  } else {
    return localeString.cardNotEligibleText;
  }
}

export {
  parseSdkNextActionError,
  parseEligibilitySurchargeDetails,
  parseEligibilityAmountDetails,
  parseEligibilityOfferDetails,
  parseEligibilityResponse,
  performEligibilityCheck,
  startEligibilityCheck,
  getCardEligibilityErrorText,
}
/* Utils-OrcaPaymentPage Not a pure module */
